/**
 * WebRTC API methods — uses the shared axios instance with auth-token + encrypt/decrypt middleware.
 */
import { api } from "../../api/api";

export interface IWebrtcPublicConfig {
  wsServer: string;
  wsHost: string;
  wsPort: number;
  wsPath: string;
  wsSecure: boolean;
  iceServers: Array<{ urls: string | string[] }>;
}

export interface IWebrtcConfig {
  server: string;
  wsServer: string;
  wsHost: string;
  wsPort: number;
  wsPath: string;
  wsSecure: boolean;
  sipUri: string;
  username: string;
  displayName: string;
  iceServers: Array<{ urls: string | string[] }>;
  password: string;
}

export interface IWebrtcEnableResponse {
  success: boolean;
  message: string;
  data?: any;
}

export interface IWebrtcTestCredentials {
  success: boolean;
  message: string;
  data?: {
    rawPayload: any;
    normalizedCredentials: any;
  };
}

/**
 * GET /webrtc/public-config — WS + ICE servers only (no credentials).
 * Safe to call at app startup.
 */
export const getWebrtcPublicConfig = async (): Promise<IWebrtcPublicConfig> => {
  const res = await api.get("/webrtc/public-config");
  return res.data.data;
};

/**
 * GET /webrtc/config/:number — Full connection block with SIP creds.
 */
export const getWebrtcConfig = async (
  extensionNumber: string
): Promise<IWebrtcConfig> => {
  const res = await api.get(`/webrtc/config/${extensionNumber}`);
  return res.data.data;
};

/**
 * PATCH /webrtc/enable/:id — Enforce PJSIP + WebRTC=Yes + SRTP on extension.
 * Admin-only action.
 */
export const enableWebrtcOnExtension = async (
  extensionId: number | string
): Promise<IWebrtcEnableResponse> => {
  const res = await api.patch(`/webrtc/enable/${extensionId}`);
  return res.data;
};

/**
 * GET /webrtc/test-credentials/:number — Diagnostic endpoint.
 * Dev/support only — should be tree-shaken from production bundle.
 */
export const testWebrtcCredentials = async (
  extensionNumber: string
): Promise<IWebrtcTestCredentials | null> => {
  if (process.env.NODE_ENV !== "development") {
    console.warn("[webrtcApi] testWebrtcCredentials is only available in dev mode");
    return null;
  }
  const res = await api.get(`/webrtc/test-credentials/${extensionNumber}`);
  return res.data;
};
